import { Permissions } from '../../../../models/enums/permissions';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import * as Forms from '../../../../components/form-elements/_index';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { StateListDisplayNames } from '../../../../components/utility/states';
import { useRouteMiss } from '../../../../hooks/use-route-miss';
import { useServiceCenter } from '../../../../hooks/use-service-center';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { toShortDate } from '../../../../utils/date-utils';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { FleetActionEditAsset } from '../components/FleetActionEditAsset';
import { AssetStatus } from '../../../../transportation/models/enums/asset-status';
import { AssetType, AssetTypeMetadata } from '../../../../transportation/models/enums/asset-type';
import { TractorMakeMetadata } from '../../../../transportation/models/enums/tractor-make';
import { TractorTypeMetadata } from '../../../../transportation/models/enums/tractor-type';
import { TrailerMakeMetadata } from '../../../../transportation/models/enums/trailer-make';
import { TrailerTypeMetadata } from '../../../../transportation/models/enums/trailer-type';
import { AssetRead, TractorRead, TrailerRead } from '../../../../transportation/models/fleet/asset-read';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps {
    location: any;
    match: any;
}

export const EditAssetScene = (props: IProps) => {

    const userServiceCenter                     = useServiceCenter();
    const [service]                             = React.useState(new TransportationSvc());
    const [asset, setAsset]                     = React.useState(new AssetRead());
    const [editAsset, setEditAsset]             = React.useState({} as AssetRead);
    const [showInfoFlyout, setShowInfoFlyout]   = React.useState(false);

    useRouteMiss(
        Routes.SHIPPING_FLEET_ASSETS,
        transportActions.clearAssetSearch()
    );

    /**
     * Go fetch the provided asset.  If no ID is passed in
     * new up an AssetRead for editing purposes
     * @param assetId The optional asset to go fetch
     */
    async function getAsset(assetId: number) {

        if (assetId) {
            const response = await service.getAsset(assetId);
            if (response.status === StatusCode.Success) {
                setAsset(response.data);
                return;
            }
        }
        
        const a = new AssetRead();
        if (userServiceCenter.name !== 'ALL') {
            a.serviceCenterId = userServiceCenter.name;
        }

        setAsset(a);
        onFlyoutShow(a);
    }

    /**
     * Respond to changes in the location
     */
    React.useEffect(() => {
        const opts = props.location.pathname.split(Routes.SHIPPING_FLEET_ASSETS_EDIT + '/');
        getAsset(opts[1]);

    }, [props.location]);

    const onFlyoutShow = (a : AssetRead) => {
        const cloned = (a && a.serviceCenterId) ? cloneDeep(a) : cloneDeep(asset);
        setEditAsset(cloned);
        setShowInfoFlyout(true);
    };

    const onFlyoutSuccess = async (result : AssetRead) => {
        if (result && asset.id) {            
            setEditAsset({} as unknown as AssetRead);
            setShowInfoFlyout(false);
            await getAsset(asset.id);
        }
        else if (result) {
            setEditAsset({} as unknown as AssetRead);
            setShowInfoFlyout(false);
            navigateToPage(Routes.SHIPPING_FLEET_ASSETS_EDIT_WITH_ID.formattify(result.id));
        }
    };
    
    const onFlyoutClose = async () => {
        setShowInfoFlyout(false);
    };

    return !AppUtils.IsEmpty(asset) 
        ? <PageContent classes="edit-asset is-in-tabs" pageName={PageNames.TRANSPORTATION_FLEET_ASSETS_DETAILS}>
            <PageHeading 
                shouldStick={ true } 
                showBackBtn={ true }
                onBackClick={ () => navigateToPage(Routes.SHIPPING_FLEET_ASSETS) }
                title={asset.id > 0 ? asset.name : 'Create Asset'}>
                <Forms.PageActions
                    id="asset-edit-form"
                    onEdit={ onFlyoutShow }
                    editPermissions={[
                        Permissions.ShippingFleetAssetsCreate,
                        Permissions.ShippingFleetAssetsEdit
                    ]}
                    deletePermissions={[]}
                    onDelete={ () => { return; }}
                />       
            </PageHeading>
            <PageBody>

                <div className="row">
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="Asset Type"
                            value={ asset.type !== undefined ? AssetTypeMetadata[asset.type].display : '' } />
                    </div>                    
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="Service Center"
                            value={ asset.serviceCenterId } />
                    </div>
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="Asset Name"
                            value={ asset.name } />
                    </div>
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="Year"
                            value={ asset.year } />
                    </div>
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="Axle Count"
                            value={ asset.axleCount } />
                    </div>
                    <div className="col-6">
                        <Forms.ReadView
                            label="Status"
                            value={
                                asset.status === AssetStatus.Active   
                                    ? <span className="asset-status active">ACTIVE</span>
                                    : <span className="asset-status inactive">INACTIVE</span>
                            }
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <DataGroupHeader title="License Information" />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="License Plate State"
                            value={ StateListDisplayNames[asset.licensePlateState] } />
                    </div>
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="License Plate Number"
                            value={ asset.licensePlateNumber } />
                    </div>
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="VIN"
                            value={ asset.vin } />
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <DataGroupHeader title="Service Dates" />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="In-Service Date"
                            value={ asset.inServiceDate ? toShortDate(asset.inServiceDate) : 'Not Provided' } />
                    </div>
                    <div className="col-md-6">
                        <Forms.ReadView
                            label="Out of Service Date"
                            value={ asset.outOfServiceDate ? toShortDate(asset.outOfServiceDate) : 'Not Provided' } />
                    </div>
                </div>

                {
                    asset.type === AssetType.Trailer
                        ? <React.Fragment>
                            <div className="row">
                                <div className="col-12">
                                        <DataGroupHeader title="Trailer Information" />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <Forms.ReadView
                                            label="Trailer Make"
                                            value={ (asset as TrailerRead).trailerMake !== undefined ? TrailerMakeMetadata[(asset as TrailerRead).trailerMake].display : 'Not Provided' } />
                                    </div>
                                    <div className="col-md-6">
                                        <Forms.ReadView
                                            label="Trailer Type"
                                            value={ (asset as TrailerRead).trailerType !== undefined ? TrailerTypeMetadata[(asset as TrailerRead).trailerType].display : 'Not Provided' } />
                                    </div>
                                </div>
                        </React.Fragment>
                        : null
                }
                
                {
                    asset.type === AssetType.Tractor
                        ? <React.Fragment>
                            <div className="row">
                                <div className="col-12">
                                        <DataGroupHeader title="Tractor Information" />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <Forms.ReadView
                                            label="Trailer Make"
                                            value={ (asset as TractorRead).tractorMake !== undefined ? TractorMakeMetadata[(asset as TractorRead).tractorMake].display : 'Not Provided' } />
                                    </div>
                                    <div className="col-md-6">
                                        <Forms.ReadView
                                            label="Trailer Type"
                                            value={ (asset as TractorRead).tractorType !== undefined ? TractorTypeMetadata[(asset as TractorRead).tractorType].display : 'Not Provided' } />
                                    </div>
                                </div>
                        </React.Fragment>
                        : null
                }                

                <FleetActionEditAsset
                    visible={ showInfoFlyout }
                    onSuccess={ onFlyoutSuccess }
                    onClose={ onFlyoutClose }
                    asset={ editAsset }
                />

            </PageBody>
        </PageContent>
        : null;

};
